// Catalog.ID Client — Claim Status Section
// ============================================================================
// Reusable helper that renders validation status badge and certificate history
// into a FieldGroup. Used by operator review, shared claim view, and anywhere
// a claim's validation state needs to be displayed outside the claim detail pane.

import { createElement, createFieldGroup, createFormRow } from '@catalog-shell';
import { createCertificateBadge, deriveValidationStatus } from './certificate-badge';
import type { ClaimDetail } from '../api/types';

/**
 * Render a validation status row + collapsible certificates section into the given container.
 * Call after fetching a ClaimDetail from the API.
 */
export function renderClaimStatusSection(container: HTMLElement, detail: ClaimDetail): void {
    // Validation status badge
    const statusGroup = createFieldGroup({ title: 'Validation Status' });

    const statusRow = createFormRow({ label: 'Status' });
    const status = deriveValidationStatus(detail.validationStatus, detail.certificates, detail.revokedAt);
    const activeCert = detail.certificates.find(c => c.isActive);
    statusRow.content.appendChild(createCertificateBadge({
        status,
        expiresAt: activeCert?.expiresAt,
        method: activeCert?.validationType,
    }));
    statusGroup.body.appendChild(statusRow.element);

    container.appendChild(statusGroup.element);

    // Certificate history (collapsible, only if certificates exist)
    if (detail.certificates.length > 0) {
        const certGroup = createFieldGroup({
            title: `Certificates (${detail.certificates.length})`,
            collapsible: true,
            collapsed: true,
            labelSize: 'm',
        });

        for (let i = 0; i < detail.certificates.length; i++) {
            const cert = detail.certificates[i];

            let method = cert.validationType;
            let confidence: number | null = null;
            let validatorId: string | null = null;
            try {
                const parsed = JSON.parse(cert.certificateData);
                const content = parsed.content ?? parsed;
                method = content.validation?.method ?? content.validation?.type ?? cert.validationType;
                confidence = content.validation?.confidence ?? null;
                validatorId = content.validator_id ?? null;
            } catch { /* use defaults */ }

            const certExpired = cert.expiresAt ? new Date(cert.expiresAt) < new Date() : false;
            const statusLabel = cert.isActive ? (certExpired ? 'Expired' : 'Active') : 'Inactive';
            const statusColor = cert.isActive ? (certExpired ? '#ea4' : '#5c5') : '#888';

            const headerRow = createFormRow({ label: 'Type' });
            headerRow.content.appendChild(createElement('span', { textContent: method }));
            headerRow.content.appendChild(createElement('span', {
                textContent: ` — ${statusLabel}`,
                style: { color: statusColor, marginLeft: '8px' },
            }));
            certGroup.body.appendChild(headerRow.element);

            const issuedRow = createFormRow({ label: 'Issued' });
            issuedRow.content.textContent = new Date(cert.issuedAt).toLocaleString();
            certGroup.body.appendChild(issuedRow.element);

            const expiresRow = createFormRow({ label: 'Expires' });
            expiresRow.content.textContent = cert.expiresAt
                ? new Date(cert.expiresAt).toLocaleString()
                : 'Never';
            certGroup.body.appendChild(expiresRow.element);

            if (confidence !== null) {
                const confRow = createFormRow({ label: 'Confidence' });
                confRow.content.textContent = `${confidence}%`;
                certGroup.body.appendChild(confRow.element);
            }

            if (validatorId) {
                const valRow = createFormRow({ label: 'Validator' });
                valRow.content.textContent = validatorId;
                valRow.content.style.fontSize = '11px';
                valRow.content.style.opacity = '0.7';
                certGroup.body.appendChild(valRow.element);
            }

            if (i < detail.certificates.length - 1) {
                certGroup.body.appendChild(createElement('hr', {
                    style: { border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' },
                }));
            }
        }

        container.appendChild(certGroup.element);
    }
}
